import * as React from "react";
import { cn } from "../../lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoResize, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const assignedRef = ref || textareaRef;
    
    // Auto-resize functionality
    const handleResize = React.useCallback(() => {
      if (!autoResize || !textareaRef.current) return;
      
      // Reset height to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      
      // Set the height to match the content
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }, [autoResize]);
    
    // Handle input changes for auto-resize
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (props.onChange) props.onChange(e);
      if (autoResize) handleResize();
    };
    
    // Initialize height on mount and when value changes
    React.useEffect(() => {
      if (autoResize) handleResize();
    }, [autoResize, props.value, handleResize]);
    
    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={assignedRef as React.RefObject<HTMLTextAreaElement>}
        onChange={handleChange}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea };